import { memo, useRef, useState, useCallback } from 'react'
import SmallArrowIcon from 'assets/icons/arrows/Select'
import MoreOptionsIcon from 'assets/icons/MoreOptions'
import FloatingCard from 'components/common/Card/Floating'
import { ownRegistration } from 'services/Registrations'
import LockIcon from 'assets/icons/Lock'
import styled from 'styled-components'
import { usePatientRouteParams } from 'components/patient/Parameters'
import { useTranslation } from 'services/Translation'

import Status from './Status'
import MoreCard from './MoreCard'
import SkippedQuestionCard from './SkippedQuestionCard'

export const BtnContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 2rem;
`

// Ill / Not-ill (incapacitated) label disabled
// const IncapacitatedLabel = styled.span<{ isSick: boolean }>`
//   font-size: 0.8125rem;
//   font-family: coresanscr65, sans-serif;
//   margin-left: 12px;
//   padding: 2px 8px;
//   border-radius: 4px;
//   color: ${({ isSick }) => (isSick ? '#DC2626' : '#10B981')};
//   background: ${({ isSick }) => (isSick ? '#FEF2F2' : '#F0FDF4')};
//   align-self: center;
//   line-height: 1;
//   white-space: nowrap;
// `

const appointmentTexts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    declaredIncapacitated: getText('Arbeidsongeschikt verklaard', 'Patient_Details_Status'),
    declaredNotIncapacitated: getText('Niet arbeidsongeschikt verklaard', 'Patient_Details_Status'),
    skippedQuestions: getText('Skipped questions', 'Patient_Details_Tabs_Summary_Form'),
  }[key])

interface AppointmentProps {
  onEdit: () => void
  editing: boolean
}

const Appointment = ({ onEdit, editing }: AppointmentProps) => {
  const skippedQuestionsRef = useRef<any>(null)
  const moreOptionsRef = useRef<any>(null)
  const [skippedQuestionsOpen, setSkippedQuestionsOpen] = useState(false)
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false)
  const { registration } = usePatientRouteParams()
  const { t } = useTranslation(appointmentTexts)

  const handleSkippedQuestions = useCallback(() => {
    setSkippedQuestionsOpen(v => !v)
  }, [])

  const handleMoreOptions = useCallback(() => {
    setMoreOptionsOpen(v => !v)
  }, [])

  return (
    <div className="flex flex-row items-center justify-between font-csc55 border-b border-grey-100 py-6 pl-6">
      <Status />
      {registration && ownRegistration(registration) && <LockIcon className="" />}
      {/* Ill / Not-ill (incapacitated) label disabled */}
      {/* {summary?.sick_label && (
        <IncapacitatedLabel isSick={summary.sick_label === 'sick'} style={{ marginLeft: 12 }}>
          {summary.sick_label === 'sick'
            ? t('declaredIncapacitated')
            : t('declaredNotIncapacitated')}
        </IncapacitatedLabel>
      )} */}
      <BtnContainer>
        <div
          className="flex items-center justify-center font-csc65 text-blue-900 text-sm underline cursor-pointer"
          ref={skippedQuestionsRef}
          onClick={handleSkippedQuestions}
          tabIndex={0}
          role="button"
          onKeyDown={handleSkippedQuestions}
        >
          {t('skippedQuestions')}
          <SmallArrowIcon className="ml-3" style={undefined} />
        </div>
        <div
          className="ml-5 flex items-center justify-center font-csc65 text-blue-900 text-sm underline cursor-pointer"
          ref={moreOptionsRef}
          onClick={handleMoreOptions}
          tabIndex={0}
          role="button"
          onKeyDown={handleMoreOptions}
          style={{ padding: '12px' }}
        >
          <MoreOptionsIcon />
        </div>
      </BtnContainer>

      <FloatingCard
        active={skippedQuestionsOpen}
        setActive={handleSkippedQuestions}
        top={
          skippedQuestionsRef?.current &&
          skippedQuestionsRef.current.getBoundingClientRect().bottom + window.scrollY + 4
        }
        left={
          skippedQuestionsRef?.current &&
          skippedQuestionsRef.current.getBoundingClientRect().left + window.scrollX
        }
        arrowPos="top"
        paddingHorizontal={undefined}
        paddingVetical={undefined}
        style={undefined}
      >
        <SkippedQuestionCard />
      </FloatingCard>
      <FloatingCard
        active={moreOptionsOpen}
        setActive={handleMoreOptions}
        top={
          moreOptionsRef?.current &&
          moreOptionsRef.current.getBoundingClientRect().bottom + window.scrollY + 4
        }
        left={
          moreOptionsRef?.current &&
          moreOptionsRef.current.getBoundingClientRect().left + window.scrollX
        }
        arrowPos="top"
        paddingHorizontal={undefined}
        paddingVetical={undefined}
        style={undefined}
      >
        <MoreCard onEdit={onEdit} editing={editing} />
      </FloatingCard>
    </div>
  )
}

Appointment.whyDidYouRender = true

export default memo(Appointment)
